import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faExclamationCircle,
  faInfoCircle,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import type { RootState } from '../../redux/store';
import type { ConfirmDialogProps } from './types';
import {
  DEFAULT_TITLE,
  DEFAULT_MESSAGE,
  DEFAULT_CONFIRM_TEXT,
  DEFAULT_CANCEL_TEXT,
  DEFAULT_CONFIRM_COLOR_SCHEME,
  DEFAULT_WIDTH,
  DEFAULT_CENTERED,
  DEFAULT_CLOSABLE,
  DEFAULT_MASK_CLOSABLE,
  DEFAULT_LOADING,
} from './constants';
import {
  StyledModal,
  TitleContainer,
  IconWrapper,
  MessageContainer,
  FooterContainer,
  StyledCancelButton,
  StyledConfirmButton,
} from './styles';

const COLOR_SCHEME_ICON_MAP: Record<string, typeof faExclamationTriangle> = {
  red: faExclamationTriangle,
  blue: faInfoCircle,
  green: faCheckCircle,
  orange: faExclamationCircle,
  purple: faInfoCircle,
};

const getIconForColorScheme = (colorScheme: string): typeof faExclamationTriangle => {
  return COLOR_SCHEME_ICON_MAP[colorScheme] || faExclamationTriangle;
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  confirmText = DEFAULT_CONFIRM_TEXT,
  cancelText = DEFAULT_CANCEL_TEXT,
  confirmColorScheme = DEFAULT_CONFIRM_COLOR_SCHEME,
  leastDestructiveRef,
  loading = DEFAULT_LOADING,
  width = DEFAULT_WIDTH,
  centered = DEFAULT_CENTERED,
  closable = DEFAULT_CLOSABLE,
  maskClosable = DEFAULT_MASK_CLOSABLE,
}) => {
  const { isDarkMode } = useSelector((state: RootState) => state.theme);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && leastDestructiveRef?.current) {
      leastDestructiveRef.current.focus();
    } else if (isOpen && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [isOpen, leastDestructiveRef]);

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onClose();
  };

  const icon = getIconForColorScheme(confirmColorScheme);

  const theme: DefaultTheme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  return (
    <ThemeProvider theme={theme}>
      <StyledModal
        open={isOpen}
        onCancel={handleCancel}
        width={width}
        centered={centered}
        closable={closable}
        maskClosable={maskClosable}
        footer={null}
        destroyOnClose
      >
        <TitleContainer>
          <IconWrapper $colorScheme={confirmColorScheme}>
            <FontAwesomeIcon icon={icon} />
          </IconWrapper>
          <span>{title}</span>
        </TitleContainer>
        <MessageContainer>{message}</MessageContainer>
        <FooterContainer>
          <StyledCancelButton
            ref={(leastDestructiveRef as React.RefObject<HTMLButtonElement>) || cancelButtonRef}
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelText}
          </StyledCancelButton>
          <StyledConfirmButton
            $colorScheme={confirmColorScheme}
            onClick={handleConfirm}
            loading={loading}
            danger={confirmColorScheme === 'red'}
          >
            {confirmText}
          </StyledConfirmButton>
        </FooterContainer>
      </StyledModal>
    </ThemeProvider>
  );
};

export default React.memo(ConfirmDialog);
